/**
 * Error Table Utilities
 *
 * Shared utilities for parsing, classifying, and transforming error data
 * into a common `ErrorRow` format used by the `ErrorTable` component.
 *
 * Supports two data sources:
 * - Version upload errors (structured JSONB `error_details` or regex-parsed `error_message`)
 * - Operation events (structured `OperationEvent[]` from DuckDB API callbacks)
 */

import type {
  VersionErrorDetails,
  VersionErrorCategory,
  OperationEvent,
  ValidationErrorDetails,
} from '@loader/shared-types';
import { formatDataType, isVersionErrorDetails } from '@loader/shared-types';

// ============================================================================
// TYPES
// ============================================================================

/** A single row in the error table */
export interface ErrorRow {
  field: string;
  type: string;
  issue: string;
}

/** Internal representation used for rendering version errors */
interface ParsedError {
  category: VersionErrorCategory;
  details: string[];
  expectedColumns?: string[];
  missingColumns?: string[];
  truncated?: boolean;
  affectedFields?: Array<{
    name: string;
    reason?: string;
    toType?: string;
    count?: number;
    sampleRows?: number[];
    lines?: Array<{ row: number; value?: string; versionNumber?: number }>;
  }>;
}

// ============================================================================
// ERROR CLASSIFICATION
// ============================================================================

const CATEGORY_BADGE: Record<VersionErrorCategory, string> = {
  schema_mismatch: 'Schema mismatch',
  data_validation: 'Validation error',
  append_conflict: 'Append conflict',
  conversion: 'Type error',
  parse: 'Parse error',
  timeout: 'Timeout',
  storage: 'Storage error',
  connection: 'Connection error',
  access: 'Access denied',
  unknown: 'Error',
};

function classifyErrorMessage(msg: string): VersionErrorCategory {
  const lower = msg.toLowerCase();

  if (lower.includes('append mode failed') || lower.includes('uniqueness conflict')) return 'append_conflict';
  if (lower.includes('schema mismatch') || lower.includes('does not appear to be a valid data file')) return 'schema_mismatch';
  if (lower.includes('missing') && lower.includes('column')) return 'schema_mismatch';
  if (lower.includes('data validation failed') || lower.includes('null value') || lower.includes('duplicate value')) return 'data_validation';
  if (lower.includes('conversion error') || (lower.includes('type') && (lower.includes('conversion') || lower.includes('mismatch')))) return 'conversion';
  if (lower.includes('parse') || lower.includes('invalid') || lower.includes('csv') || lower.includes('could not convert') || lower.includes('line ')) return 'parse';
  if (lower.includes('timeout')) return 'timeout';
  if (lower.includes('r2') || lower.includes('s3') || lower.includes('storage')) return 'storage';
  if (lower.includes('connection') || lower.includes('network')) return 'connection';
  if (lower.includes('permission') || lower.includes('access denied') || lower.includes('forbidden')) return 'access';

  return 'unknown';
}

function getShortErrorReason(errorMessage: string, errorDetails?: Record<string, unknown> | null): string {
  if (errorDetails?.category && typeof errorDetails.category === 'string') {
    const badge = CATEGORY_BADGE[errorDetails.category as VersionErrorCategory];
    if (badge) return badge;
  }
  return CATEGORY_BADGE[classifyErrorMessage(errorMessage)];
}

/**
 * Get a badge label like "15 errors" or "100+ errors" from error details.
 * Falls back to the short error reason if count cannot be determined.
 */
export function getErrorCountLabel(errorMessage: string, errorDetails?: unknown): string {
  if (errorDetails && isVersionErrorDetails(errorDetails)) {
    const truncated = errorDetails.truncated ?? false;
    let count = 0;

    if (errorDetails.missingColumns?.length) {
      count += errorDetails.missingColumns.length;
    }
    if (errorDetails.fields?.length) {
      for (const f of errorDetails.fields) {
        count += f.lines?.length ?? f.count ?? 1;
      }
    }

    if (count > 0) {
      return truncated ? `${count}+ errors` : `${count} error${count !== 1 ? 's' : ''}`;
    }
  }

  return getShortErrorReason(errorMessage, errorDetails as Record<string, unknown> | null);
}

// ============================================================================
// ERROR PARSING — JSONB (preferred) or regex fallback
// ============================================================================

export function parseError(errorMessage: string, errorDetails?: unknown): ParsedError {
  if (errorDetails && isVersionErrorDetails(errorDetails)) {
    return parseFromDetails(errorDetails, errorMessage);
  }
  return parseFromMessage(errorMessage);
}

/** Convenience helper: parse + convert to rows + extract truncated flag in one call */
export function parseErrorToRows(errorMessage: string, errorDetails?: unknown): { rows: ErrorRow[]; isTruncated: boolean } {
  const parsed = parseError(errorMessage, errorDetails);
  return { rows: toErrorRows(parsed), isTruncated: parsed.truncated ?? false };
}

/** Parse from the structured error_details JSONB column */
function parseFromDetails(details: VersionErrorDetails, errorMessage: string): ParsedError {
  const parsed: ParsedError = {
    category: details.category,
    details: [],
    truncated: details.truncated,
  };

  switch (details.category) {
    case 'schema_mismatch':
      parsed.missingColumns = details.missingColumns;
      parsed.expectedColumns = details.expectedColumns;
      break;
    case 'data_validation':
    case 'append_conflict':
    case 'conversion':
      if (details.fields) {
        parsed.affectedFields = details.fields.map((f) => ({
          name: f.name,
          reason: f.reason,
          toType: f.toType,
          count: f.count,
          sampleRows: f.sampleRows,
          lines: f.lines as Array<{ row: number; value?: string; versionNumber?: number }> | undefined,
        }));
        parsed.details = details.fields.map((f) => f.reason);
      }
      break;
    default:
      parsed.details = [errorMessage];
      break;
  }

  return parsed;
}

/** Fall back: parse a raw error message via regex into structured sections */
function parseFromMessage(errorMessage: string): ParsedError {
  const category = classifyErrorMessage(errorMessage);
  const parsed: ParsedError = { category, details: [] };

  switch (category) {
    case 'schema_mismatch': {
      const missingMatch = errorMessage.match(/missing \d+ required column\(s\):\s*([^.]+)/i);
      if (missingMatch) parsed.missingColumns = missingMatch[1].split(',').map((s) => s.trim());

      const expectedMatch = errorMessage.match(/Expected columns:\s*([^.]+)\./i);
      if (expectedMatch) parsed.expectedColumns = expectedMatch[1].split(',').map((s) => s.trim());

      if (!parsed.missingColumns && !parsed.expectedColumns) parsed.details = [errorMessage];
      break;
    }
    case 'data_validation': {
      const fieldErrors = errorMessage
        .replace(/^Data validation failed:\s*/i, '')
        .split(/\.\s*/)
        .map((s) => s.trim())
        .filter(Boolean);

      const fields: Array<{ name: string; reason: string }> = [];
      for (const err of fieldErrors) {
        const fieldMatch = err.match(/Field "([^"]+)"/);
        if (fieldMatch) fields.push({ name: fieldMatch[1], reason: err });
      }
      parsed.affectedFields = fields.length > 0 ? fields : undefined;
      parsed.details = fieldErrors;
      break;
    }
    case 'append_conflict': {
      const parts = errorMessage.split(/;\s*/);
      const fields: Array<{ name: string; reason: string }> = [];
      for (const part of parts) {
        const fieldMatch = part.match(/Field "([^"]+)"/);
        if (fieldMatch) fields.push({ name: fieldMatch[1], reason: part.trim() });
      }
      parsed.affectedFields = fields.length > 0 ? fields : undefined;
      parsed.details = parts.map((s) => s.trim()).filter(Boolean);
      break;
    }
    default:
      parsed.details = [errorMessage];
      break;
  }

  return parsed;
}

// ============================================================================
// ERROR → TABLE ROWS (per-line expansion)
// ============================================================================

function getErrorType(reason: string, category: string): string {
  if (reason.includes('null value')) return 'Null value';
  if (reason.includes('duplicate value')) return 'Duplicate';
  if (category === 'append_conflict') return 'Conflict';
  if (category === 'conversion') return 'Type error';
  return 'Error';
}

export function toErrorRows(parsed: ParsedError): ErrorRow[] {
  const rows: ErrorRow[] = [];

  if (parsed.missingColumns && parsed.missingColumns.length > 0) {
    for (const col of parsed.missingColumns) {
      rows.push({ field: col, type: 'Missing column', issue: 'Column missing from uploaded file' });
    }
  }

  if (parsed.affectedFields && parsed.affectedFields.length > 0) {
    for (const f of parsed.affectedFields) {
      const reason = f.reason ?? '';
      const isNull = reason.includes('null value');
      const isDuplicate = reason.includes('duplicate value');
      const type = getErrorType(reason, parsed.category);

      if (f.lines && f.lines.length > 0) {
        for (const line of f.lines) {
          const vPrefix = line.versionNumber ? `v${line.versionNumber} · ` : '';
          if (isNull) {
            rows.push({ field: f.name, type, issue: `${vPrefix}Row ${line.row}: Value cannot be null` });
          } else if (isDuplicate && line.value !== undefined) {
            rows.push({ field: f.name, type, issue: `${vPrefix}Row ${line.row}: Duplicate value "${line.value}"` });
          } else if (parsed.category === 'append_conflict' && line.value !== undefined) {
            rows.push({ field: f.name, type, issue: `${vPrefix}Row ${line.row}: Value "${line.value}" already exists` });
          } else if (parsed.category === 'conversion' && line.value !== undefined) {
            const typeLabel = f.toType ? formatDataType(f.toType) : undefined;
            rows.push({ field: f.name, type, issue: `${vPrefix}Row ${line.row}: Cannot convert "${line.value}"${typeLabel ? ` [${typeLabel}]` : ''}` });
          } else {
            rows.push({ field: f.name, type, issue: `${vPrefix}Row ${line.row}: ${reason}` });
          }
        }
      } else if (f.sampleRows && f.sampleRows.length > 0) {
        for (const row of f.sampleRows) {
          if (isNull) {
            rows.push({ field: f.name, type, issue: `Row ${row}: Value cannot be null` });
          } else if (isDuplicate) {
            rows.push({ field: f.name, type, issue: `Row ${row}: Value must be unique` });
          } else {
            rows.push({ field: f.name, type, issue: `Row ${row}: ${reason}` });
          }
        }
      } else {
        rows.push({ field: f.name, type, issue: reason });
      }
    }
  }

  if (rows.length === 0 && parsed.details.length > 0) {
    for (const detail of parsed.details) {
      rows.push({ field: '-', type: 'Error', issue: detail });
    }
  }

  return rows;
}

// ============================================================================
// OPERATION EVENTS → ERROR ROWS
// ============================================================================

/** Map operation events (error/warning levels) to ErrorRow[] for the ErrorTable */
export function operationEventsToErrorRows(events: OperationEvent[]): ErrorRow[] {
  const errorLevels = new Set(['error', 'validation_error', 'warning', 'coercion_warning']);
  const errorEvents = events.filter((e) => errorLevels.has(e.level));

  return errorEvents.map((event) => {
    const details = event.details as Partial<ValidationErrorDetails> | null;
    const field = event.field_name ?? '-';
    const type = event.level === 'warning' || event.level === 'coercion_warning' ? 'Warning' : 'Error';

    let issue = event.message;
    if (event.row_number) {
      issue = `Row ${event.row_number}: ${issue}`;
    }
    if (details?.expected_value && details?.actual_value) {
      issue += ` (expected: ${details.expected_value}, got: ${details.actual_value})`;
    } else if (details?.expected_value) {
      issue += ` (expected: ${details.expected_value})`;
    } else if (details?.actual_value) {
      issue += ` (got: ${details.actual_value})`;
    }

    return { field, type, issue };
  });
}

// ============================================================================
// ERROR COUNT FORMATTING
// ============================================================================

/** Format error count for display in badges and footers */
export function formatErrorCount(count: number, isTruncated?: boolean): string {
  if (count === 0) return 'Failed';
  return isTruncated ? `${count}+ errors` : `${count} error${count !== 1 ? 's' : ''}`;
}

// ============================================================================
// XLSX EXPORT
// ============================================================================

export async function downloadXlsx(rows: ErrorRow[], fileName: string) {
  try {
    const XLSX = await import('xlsx');
    const data = rows.map((r) => ({ Field: r.field, Type: r.type, Details: r.issue }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Errors');
    XLSX.writeFile(wb, fileName);
  } catch (err) {
    console.warn('XLSX export failed, falling back to CSV', err);
    const csv = ['Field,Type,Details', ...rows.map((r) => `"${r.field}","${r.type}","${r.issue.replace(/"/g, '""')}"`)]
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName.replace(/\.xlsx$/, '.csv');
    a.click();
    URL.revokeObjectURL(url);
  }
}
